import { useMemo } from 'react';

import { highlightCodeLines, normalizeLanguage } from '@/shared/lib/code-highlight';

/**
 * Token-styled lines for a fenced block, a frozen file or a cited excerpt.
 * Colours come from the `.hljs-*` rules in globals.css, scoped by an ancestor
 * carrying `prose-mio` or `code-highlight`. Never renders generated HTML.
 */
export function CodeLines({
  code,
  language,
  lineNumbers = false,
  firstLine = 1,
  codeClassName,
}: {
  code: string;
  language?: string | undefined;
  lineNumbers?: boolean;
  /** Number shown for the first line, so a cited excerpt keeps its file position. */
  firstLine?: number;
  codeClassName?: string | undefined;
}) {
  const grammar = normalizeLanguage(language);
  const lines = useMemo(() => highlightCodeLines(code, grammar), [code, grammar]);
  return (
    <code className={codeClassName ?? `language-${grammar}`}>
      {lines.map((line, index) => (
        <span key={index} className="block">
          {lineNumbers ? (
            <span
              aria-hidden="true"
              className="text-fg-muted mr-4 inline-block w-8 select-none text-right"
            >
              {firstLine + index}
            </span>
          ) : null}
          {line.length === 0
            ? '\u200b'
            : line.map((token, position) =>
                token.className ? (
                  <span key={position} className={token.className}>
                    {token.text}
                  </span>
                ) : (
                  token.text
                ),
              )}
        </span>
      ))}
    </code>
  );
}
